import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../../redux/slices/appInfo';
import { Button } from 'antd';
import { ICGoogle, ICFacebook } from '../../../icons/index';
import { useKeycloakAuth } from '../../../auth/useKeycloakAuth';

const Wrapper = styled.div`
    background-color: #7357ff;
    background-image: url('/images/bg-pattern.png');
    background-size: cover;
    background-repeat: no-repeat;
`;

/**
 * Google "G" sign-in button, styled per Google's identity branding guidelines:
 *   - white background, 1px #dadce0 border, #3c4043 text
 *   - 18x18 multicolor G icon, left of the label
 *   - hover: #f8f9fa background
 *   - font: 14px Google Sans / Roboto, medium weight
 * The button still triggers Keycloak login — only the visual matches Google
 * to match the renamed "Continue with Google" label. Identity is provided
 * by Keycloak with social login (Google IdP) or any other configured broker.
 */
const GoogleButton = styled(Button)`
    &&.ant-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        height: 48px;
        padding: 0 16px;
        background-color: #ffffff;
        border: 1px solid #dadce0;
        border-radius: 8px;
        color: #3c4043;
        font-family:
            'Google Sans',
            'Roboto',
            system-ui,
            -apple-system,
            'Segoe UI',
            sans-serif;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: 0.0107em;
        box-shadow: none;
        transition:
            background-color 0.15s ease,
            box-shadow 0.15s ease;
    }
    &&.ant-btn:hover,
    &&.ant-btn:focus {
        background-color: #f8f9fa;
        border-color: #dadce0;
        color: #3c4043;
        box-shadow: 0 1px 2px rgba(60, 64, 67, 0.1);
    }
    &&.ant-btn:active {
        background-color: #f1f3f4;
        border-color: #dadce0;
        color: #3c4043;
        box-shadow: 0 1px 2px rgba(60, 64, 67, 0.15);
    }
    &&.ant-btn > .anticon + span {
        margin-left: 0;
    }
`;

/**
 * Facebook "f" sign-in button, styled per Facebook's Login Button
 * branding guidelines:
 *   - Facebook brand blue (#1877F2) background, white text
 *   - 18x18 white "f" icon, left of the label
 *   - hover: #166FE5 (slightly darker)
 *   - active: #1461CC
 *   - border-radius: 8px, height 48px (matches Google button)
 *   - font: 14px Helvetica / Arial, semibold (600)
 *   - letter-spacing: 0.0107em
 * The button still triggers Keycloak login — only the visual matches
 * Facebook to match the "Continue with Facebook" label. Identity is
 * provided by Keycloak with the Facebook IdP broker.
 *
 * Source: https://developers.facebook.com/docs/facebook-login/web/login-button
 */
const FacebookButton = styled(Button)`
    &&.ant-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        height: 48px;
        padding: 0 16px;
        background-color: #ffffff;
        border: 1px solid #dadce0;
        border-radius: 8px;
        color: #3c4043;
        font-family: Helvetica, Arial, sans-serif;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0.0107em;
        box-shadow: none;
        transition:
            background-color 0.15s ease,
            box-shadow 0.15s ease;
    }
    &&.ant-btn:hover,
    &&.ant-btn:focus {
        background-color: #f8f9fa;
        border-color: #dadce0;
        color: #3c4043;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
    &&.ant-btn:active {
        background-color: #f1f3f4;
        border-color: #dadce0;
        color: #3c4043;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
    }
    &&.ant-btn > .anticon + span {
        margin-left: 0;
    }
`;

/**
 * Sign-in landing page. The actual authentication happens on Keycloak's
 * login page (redirect via `keycloak.login()`). On a successful return the
 * user lands on /auth/callback (see pages/auth/callback/index.tsx), which
 * hydrates Redux and POSTs /api/users/sync before navigating to /work-order.
 *
 * The button label is "Continue with Google" and styled to match Google's
 * identity guidelines because the AutoTrack Keycloak realm is configured
 * with Google as an identity broker (see auth.driftcar.ai/realms/autotrack
 * -> Identity Providers). Users who reach Keycloak see the Google login
 * screen, so the in-app CTA mirrors that.
 *
 * If the user is already authenticated we skip the page and go straight to
 * /work-order — common when a logged-in user clicks "Sign in" from the
 * header.
 */
export const LoginPage: React.FC = () => {
    const { isAuthenticated, login } = useKeycloakAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        // Reset the global loading overlay on (re-)mount. handleLogin /
        // handleFacebookLogin intentionally leave it ON while the redirect
        // is in flight (comment below) so the user doesn't see a flash of
        // the login card between click and Keycloak's handoff. But if
        // Keycloak bounces the user back here (cancel, IdP error, network
        // blip) the page re-mounts with `isShowLoading=true` and the
        // overlay never clears. Always start from a clean slate.
        dispatch(setLoading(false));
    }, [dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/work-order', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const handleLogin = () => {
        dispatch(setLoading(true));
        // 'google' => kc_idp_hint=google => Keycloak forwards straight to
        // the Google IdP broker, skipping the realm login page.
        login('google');
        // The redirect leaves the page, so we don't unset the loading
        // flag. If redirect is blocked for any reason the spinner will
        // stay until the next mount, which is acceptable. The mount
        // effect above clears it on re-entry (Keycloak error redirect,
        // user cancel, etc.).
    };

    const handleFacebookLogin = () => {
        dispatch(setLoading(true));
        // 'facebook' => kc_idp_hint=facebook => Keycloak forwards straight
        // to the Facebook IdP broker (configured on the autotrack realm
        // with alias "facebook"). Skips the realm login page entirely.
        login('facebook');
    };

    return (
        <Wrapper className="flex w-screen h-screen items-center justify-center bg-surface-low">
            <div className="shadow-e-03 max-md:max-w-[478px] max-md:w-auto w-[478px] p-[32px] flex flex-col gap-[24px] rounded-[12px] overflow-hidden box-border bg-white">
                <div className="flex justify-center">
                    <h1 className="text-heading-5 text-high-em font-semibold">
                        Welcome Auto Track
                    </h1>
                </div>
                <p className="text-body text-mid-em text-center">
                    Sign in with your work email to continue.
                </p>
                <div className="flex flex-col w-full gap-[12px] pt-[8px]">
                    <GoogleButton block size="large" onClick={handleLogin}>
                        <ICGoogle width={18} height={18} />
                        <span>Continue with Google</span>
                    </GoogleButton>
                    <FacebookButton
                        block
                        size="large"
                        onClick={handleFacebookLogin}
                    >
                        <ICFacebook width={18} height={18} />
                        <span>Continue with Facebook</span>
                    </FacebookButton>
                </div>
                <div className="flex gap-[8px] items-center justify-center text-center text-high-em text-body pt-[8px]">
                    <span>Need an account?</span>
                    <Link
                        to="/register"
                        className="underline text-info-500 font-medium"
                    >
                        Register
                    </Link>
                </div>
            </div>
        </Wrapper>
    );
};
